import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CLAIM_EXPIRY_MARGIN_MS,
  CLAIM_STAGGER_MS,
  ORPHAN_SWEEP_MS,
  PRESENCE_SWEEP_MS,
  PEER_STALE_MS,
  STARVATION_MS,
  TICK_MS,
  HEARTBEAT_MS,
} from '../constants/moderation'
import {
  claimVideo,
  releaseClaim,
  releaseClaims,
  releaseClaimsFast,
  removePresence,
  subscribeClaims,
  subscribeModerators,
  sweepDeadPresence,
  sweepOrphanClaims,
  writePresence,
} from '../services/moderationCoordination'
import { useServerClock } from './useServerClock'
import { modlog } from '../utils/modlog'

// The multi-moderator claiming protocol, entire.
//
// Several moderators work one growing queue with no coordinator: each runs this
// loop once a second, publishes a heartbeat, and takes videos by transaction.
// Exclusivity comes from the claim document's id being the video's id; fairness
// comes from every moderator seeing everyone's load and deliberately passing
// when someone else is further behind; recovery comes from claims and
// heartbeats expiring, because a browser that dies runs no cleanup code.
//
// See docs/superpowers/specs/2026-09-04-multi-moderator-claiming-design.md

function newSessionId() {
  try {
    return crypto.randomUUID()
  } catch {
    return `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  }
}

function emptySlots(n) {
  return Array.from({ length: n }, () => null)
}

/** A slot can take a new video when it is empty, already decided, or lost. */
function slotIsFree(slot) {
  return !slot || slot.decision !== null || slot.lost
}

function acceptsChallenge(filter, challenge) {
  return !filter || filter.length === 0 || filter.includes(challenge)
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

/**
 * @param eventId          event being moderated
 * @param user             the signed-in admin
 * @param pendingVideos    live list of unmoderated videos, oldest first
 * @param capacity         grid slots
 * @param challengeFilter  selected challenge ids ([] = all)
 * @param onDecide         (videoId, accepted) => Promise, the moderation write
 */
export function useModerationSession({
  eventId,
  user,
  pendingVideos,
  capacity,
  challengeFilter,
  onDecide,
}) {
  const clock = useServerClock()

  const [sessionId, setSessionId] = useState(newSessionId)
  const [paused, setPaused] = useState(false)
  const [moderators, setModerators] = useState([])
  const [claims, setClaims] = useState({})

  // Claim placement has to be race-free: several claims resolve at once and
  // each needs a distinct slot. The ref is authoritative and mutated
  // synchronously; the state is its mirror, for rendering.
  const slotsRef = useRef(emptySlots(capacity))
  const [slots, setSlots] = useState(slotsRef.current)
  const updateSlots = useCallback((fn) => {
    const next = fn(slotsRef.current)
    if (next === slotsRef.current) return
    slotsRef.current = next
    setSlots(next)
  }, [])

  // Anything time-dependent (peer staleness, claim expiry) has to be
  // recomputed on a clock, not only when a document changes — otherwise a
  // claim that quietly expires is not noticed until the next write lands.
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [])

  // The tick runs on an interval, so everything it reads comes from this ref or
  // it would close over the state of whichever render installed it.
  const live = useRef({})
  const inFlight = useRef(new Set())
  const firstAvailableAt = useRef(new Map())
  const lastHeartbeat = useRef(0)
  const heartbeatLandedAt = useRef(0)

  //--------------------------------------------------------------------------
  // Listeners. Deliberately attached even while paused: someone deciding
  // whether to start needs to see the queue depth and who else is working.

  useEffect(() => {
    if (!eventId) return
    const unsub = subscribeModerators(eventId, setModerators)
    return () => unsub()
  }, [eventId])

  useEffect(() => {
    if (!eventId) return
    const unsub = subscribeClaims(eventId, setClaims)
    return () => unsub()
  }, [eventId])

  // Our own heartbeat, read back carrying the server's timestamp, is the
  // clock-offset sample.
  useEffect(() => {
    const mine = moderators.find((m) => m.sessionId === sessionId)
    if (mine?.lastHeartbeatMs) clock.noteEcho(mine.lastHeartbeatMs)
  }, [moderators, sessionId, clock])

  //--------------------------------------------------------------------------
  // Grid capacity. Shrinking hands back whatever was held in the slots that
  // disappear — it cannot be shown any more, so it must not stay claimed.

  useEffect(() => {
    updateSlots((prev) => {
      if (prev.length === capacity) return prev
      if (capacity > prev.length) return [...prev, ...emptySlots(capacity - prev.length)]
      const dropped = prev
        .slice(capacity)
        .filter((s) => s && s.decision === null && !s.lost)
        .map((s) => s.video.id)
      if (dropped.length) {
        modlog('release', { sessionId, videoIds: dropped, reason: 'grid-shrunk' })
        releaseClaims(eventId, dropped, sessionId)
      }
      return prev.slice(0, capacity)
    })
  }, [capacity, eventId, sessionId, updateSlots])

  //--------------------------------------------------------------------------
  // Challenge filter: a held video that no longer matches goes back to the pool.

  useEffect(() => {
    updateSlots((prev) => {
      const dropped = prev
        .filter(
          (s) =>
            s &&
            s.decision === null &&
            !s.lost &&
            !acceptsChallenge(challengeFilter, s.video.challenge),
        )
        .map((s) => s.video.id)
      if (!dropped.length) return prev
      modlog('release', { sessionId, videoIds: dropped, reason: 'filter-changed' })
      releaseClaims(eventId, dropped, sessionId)
      const droppedSet = new Set(dropped)
      return prev.map((s) => (s && droppedSet.has(s.video.id) ? null : s))
    })
  }, [challengeFilter, eventId, sessionId, updateSlots])

  //--------------------------------------------------------------------------
  // Derived views of the shared state

  const moderatorsBySession = useMemo(
    () => Object.fromEntries(moderators.map((m) => [m.sessionId, m])),
    [moderators],
  )

  const activeSessionIds = useMemo(() => {
    const now = nowMs + clock.offset()
    return new Set(
      moderators
        .filter((m) => m.lastHeartbeatMs && now - m.lastHeartbeatMs < PEER_STALE_MS)
        .map((m) => m.sessionId),
    )
  }, [moderators, nowMs, clock])

  /**
   * Is this claim still worth respecting, and if not, why not?
   *
   * The reason is worth carrying: "its owner went quiet" and "the holder has
   * had it too long" are different events for whoever is reading the logs after
   * an event, and telling them apart is the only way to know whether a takeover
   * was the protocol working or a moderator being robbed.
   */
  const claimStatus = useCallback(
    (claim) => {
      if (!claim) return { live: false, reason: 'unclaimed' }
      if (claim.sessionId === sessionId) return { live: true, reason: 'mine' }
      // A claim whose timestamp has not materialized is somebody's very recent
      // write; treat it as live rather than racing it.
      if (!claim.claimedAtMs) return { live: true, reason: 'pending-write' }

      // Pinned to the tick rather than read fresh, so this callback's identity
      // changes once a second — which is what makes a quietly expiring claim
      // get noticed without waiting for the next write to land somewhere.
      const now = nowMs + clock.offset()
      const ageMs = now - claim.claimedAtMs
      const peer = moderatorsBySession[claim.sessionId]
      const peerAgeMs = peer?.lastHeartbeatMs ? now - peer.lastHeartbeatMs : null

      // No presence document at all: nothing to respect.
      if (!peer) return { live: false, reason: 'peer-gone', ageMs, peerAgeMs }
      // Silence is grounds for taking their work, but with the same margin the
      // TTL gets: a snapshot arriving a second late should not cost a colleague
      // the clip they are watching.
      if (peerAgeMs != null && peerAgeMs >= PEER_STALE_MS + CLAIM_EXPIRY_MARGIN_MS) {
        return { live: false, reason: 'peer-stale', ageMs, peerAgeMs }
      }
      if (ageMs >= (claim.ttlMs || 0) + CLAIM_EXPIRY_MARGIN_MS) {
        return { live: false, reason: 'expired', ageMs, peerAgeMs, ttlMs: claim.ttlMs }
      }
      return { live: true, reason: 'held', ageMs, peerAgeMs }
    },
    [nowMs, sessionId, clock, moderatorsBySession],
  )

  const isClaimLive = useCallback((claim) => claimStatus(claim).live, [claimStatus])

  /** Live claim count per session — fresher and harder to under-report than the
   *  `load` each moderator publishes, so this is what fair share uses. */
  const loadBySession = useMemo(() => {
    const counts = {}
    Object.values(claims).forEach((claim) => {
      if (!isClaimLive(claim)) return
      counts[claim.sessionId] = (counts[claim.sessionId] || 0) + 1
    })
    return counts
  }, [claims, isClaimLive])

  const peers = useMemo(
    () =>
      moderators
        .filter((m) => m.sessionId !== sessionId && activeSessionIds.has(m.sessionId))
        .map((m) => ({ ...m, load: loadBySession[m.sessionId] || 0 })),
    [moderators, sessionId, activeSessionIds, loadBySession],
  )

  const heldIds = useMemo(
    () => slots.filter((s) => s && s.decision === null && !s.lost).map((s) => s.video.id),
    [slots],
  )

  const freeSlots = useMemo(() => slots.filter(slotIsFree).length, [slots])

  const pendingById = useMemo(() => {
    const map = new Map()
    pendingVideos.forEach((v) => map.set(v.id, v))
    return map
  }, [pendingVideos])

  /** Videos I could take right now. A lost slot's video is not excluded: I gave
   *  it up, and if its new owner drops it, it is fair game again. */
  const available = useMemo(() => {
    const mine = new Set(slots.filter((s) => s && !s.lost).map((s) => s.video.id))
    return pendingVideos.filter(
      (v) =>
        acceptsChallenge(challengeFilter, v.challenge) &&
        !mine.has(v.id) &&
        !inFlight.current.has(v.id) &&
        !isClaimLive(claims[v.id]),
    )
  }, [pendingVideos, challengeFilter, slots, claims, isClaimLive])

  //--------------------------------------------------------------------------
  // Slot invalidation: a held video taken over by someone else, or decided by
  // them, becomes a "lost" tile rather than silently vanishing under the cursor.

  useEffect(() => {
    const lost = []
    updateSlots((prev) => {
      let changed = false
      const next = prev.map((slot) => {
        if (!slot || slot.decision !== null || slot.lost) return slot
        const id = slot.video.id
        const claim = claims[id]
        const stolen = claim && claim.sessionId !== sessionId && isClaimLive(claim)
        const goneFromQueue = !pendingById.has(id)
        if (!stolen && !goneFromQueue) return slot
        changed = true
        lost.push({ id, to: stolen ? claim.sessionId : null, stolen })
        return { ...slot, lost: true, lostTo: stolen ? claim.sessionId : null }
      })
      return changed ? next : prev
    })
    lost.forEach((l) =>
      modlog('lost', {
        sessionId,
        videoId: l.id,
        to: l.to,
        reason: l.stolen ? 'stolen' : 'decided-elsewhere',
      }),
    )
  }, [claims, pendingById, sessionId, isClaimLive, updateSlots])

  //--------------------------------------------------------------------------
  // Keep the tick's view of the world current

  live.current = {
    eventId,
    sessionId,
    paused,
    user,
    capacity,
    challengeFilter,
    peers,
    moderators,
    claims,
    claimStatus,
    available,
    pendingById,
    heldIds,
    freeSlots,
    myLoad: heldIds.length,
    isClaimLive,
  }

  //--------------------------------------------------------------------------
  // Fair share
  //
  // Walk the available videos oldest-first with a projected load that grows as
  // I take them, and pass on a video whenever somebody who could also take it
  // is further behind and there is not enough to go round. The starvation
  // override stops that from deadlocking: a moderator idling with free slots
  // but a challenge filter that excludes this video would otherwise reserve
  // capacity against it forever.

  const selectClaimable = useCallback((s, now, budget) => {
    const chosen = []
    let projected = s.myLoad + inFlight.current.size
    for (const video of s.available) {
      if (chosen.length >= budget) break
      // `available` is computed during render and cannot see this ref change,
      // so a claim still in flight would otherwise be selected again next tick.
      if (inFlight.current.has(video.id)) continue

      const firstSeen = firstAvailableAt.current.get(video.id) ?? now
      const starving = now - firstSeen > STARVATION_MS
      const priorStatus = s.claims[video.id] ? s.claimStatus(s.claims[video.id]) : null

      if (!starving) {
        const contenders = s.peers.filter(
          (p) => acceptsChallenge(p.challengeFilter, video.challenge) && (p.freeSlots || 0) > 0,
        )
        const behind = contenders.filter(
          (p) => p.load < projected || (p.load === projected && p.sessionId < s.sessionId),
        )
        if (behind.length > 0) {
          const reserved = behind.reduce(
            (sum, p) => sum + Math.min(p.freeSlots, Math.max(0, projected - p.load)),
            0,
          )
          const remaining = s.available.length - chosen.length
          if (remaining <= reserved) continue
        }
      }

      chosen.push({ video, starving, priorStatus })
      projected += 1
    }
    return chosen
  }, [])

  /** Put a won video in the lowest free slot. Synchronous against the ref, so
   *  several claims resolving together cannot land on the same slot. */
  const placeClaimed = useCallback(
    (video, now) => {
      // Already on screen: keep it where it is rather than showing one video in
      // two slots.
      if (slotsRef.current.some((s) => s && !s.lost && s.video.id === video.id)) return true
      const idx = slotsRef.current.findIndex(slotIsFree)
      if (idx === -1) return false
      const next = [...slotsRef.current]
      next[idx] = {
        video,
        claimedAtMs: now,
        decision: null,
        watched: false,
        lost: false,
        lostTo: null,
      }
      slotsRef.current = next
      setSlots(next)
      return true
    },
    [],
  )

  //--------------------------------------------------------------------------
  // The tick

  const tick = useCallback(async () => {
    const s = live.current
    if (s.paused || !s.eventId || !s.user?.uid) return
    const now = clock.serverNow()

    // 1. Heartbeat
    if (now - lastHeartbeat.current >= HEARTBEAT_MS) {
      lastHeartbeat.current = now
      const sentAt = Date.now()
      const beat = writePresence(s.eventId, s.sessionId, {
        uid: s.user.uid,
        displayName: s.user.displayName || s.user.email || '',
        capacity: s.capacity,
        freeSlots: s.freeSlots,
        load: s.myLoad,
        challengeFilter: s.challengeFilter || [],
      })
      beat
        .then(() => {
          clock.noteWrite((sentAt + Date.now()) / 2)
          heartbeatLandedAt.current = clock.serverNow()
          // Traced so an analysis of the timeline can tell a takeover that
          // followed a genuinely silent moderator from one that jumped the gun.
          modlog('heartbeat', { sessionId: s.sessionId, load: s.myLoad, freeSlots: s.freeSlots })
        })
        .catch((err) => modlog('heartbeat-failed', { sessionId: s.sessionId, error: String(err) }))
    }

    // A moderator that cannot reach Firestore is reading its peers from a stale
    // cache, where everyone looks silent and every claim looks abandoned. Such a
    // client must be passive: it is the one in trouble, not them. This is what
    // stops a moderator whose wifi drops from stealing the whole queue the
    // instant it comes back.
    if (now - heartbeatLandedAt.current > PEER_STALE_MS / 2) {
      if (heartbeatLandedAt.current > 0) modlog('offline-passive', { sessionId: s.sessionId })
      return
    }

    // 2. Availability ages, for the starvation override
    const availableIds = new Set(s.available.map((v) => v.id))
    availableIds.forEach((id) => {
      if (!firstAvailableAt.current.has(id)) firstAvailableAt.current.set(id, now)
    })
    firstAvailableAt.current.forEach((_, id) => {
      if (!availableIds.has(id)) firstAvailableAt.current.delete(id)
    })

    // 3. Orphan sweep — claim documents for videos that are no longer pending.
    //    One moderator does it, chosen by lowest session id, rather than all N
    //    issuing the same deletes.
    const sessionIds = [s.sessionId, ...s.peers.map((p) => p.sessionId)].sort()
    if (sessionIds[0] === s.sessionId) {
      const orphans = Object.values(s.claims)
        .filter(
          (c) => !s.pendingById.has(c.videoId) && c.claimedAtMs && now - c.claimedAtMs > ORPHAN_SWEEP_MS,
        )
        .map((c) => c.videoId)
      if (orphans.length) sweepOrphanClaims(s.eventId, orphans)

      const abandoned = s.moderators
        .filter(
          (m) =>
            m.sessionId !== s.sessionId &&
            m.lastHeartbeatMs &&
            now - m.lastHeartbeatMs > PRESENCE_SWEEP_MS,
        )
        .map((m) => m.sessionId)
      if (abandoned.length) {
        modlog('sweep-presence', { sessionId: s.sessionId, sessionIds: abandoned })
        sweepDeadPresence(s.eventId, abandoned)
      }
    }

    // 4. Claim
    const budget = s.freeSlots - inFlight.current.size
    if (budget <= 0) return
    const chosen = selectClaimable(s, now, budget)
    if (!chosen.length) return

    // The least-loaded moderator reaches Firestore first, so the common case is
    // uncontended. A collision is still correct — the transaction admits one —
    // it just costs a round-trip.
    const rank = [...s.peers, { sessionId: s.sessionId, load: s.myLoad }]
      .sort((a, b) => a.load - b.load || (a.sessionId < b.sessionId ? -1 : 1))
      .findIndex((m) => m.sessionId === s.sessionId)
    if (rank > 0) await sleep(rank * CLAIM_STAGGER_MS)

    await Promise.all(
      chosen.map(async ({ video, starving, priorStatus }) => {
        inFlight.current.add(video.id)
        try {
          const won = await claimVideo(
            s.eventId,
            video.id,
            s.sessionId,
            s.user.uid,
            video,
            s.isClaimLive,
          )
          if (!won) {
            modlog('lost-race', { sessionId: s.sessionId, videoId: video.id })
            return
          }
          const placedNow = clock.serverNow()
          if (!placeClaimed(video, placedNow)) {
            // The grid filled between selection and the claim landing.
            await releaseClaim(s.eventId, video.id, s.sessionId)
            return
          }
          firstAvailableAt.current.delete(video.id)
          modlog('claim', {
            sessionId: s.sessionId,
            videoId: video.id,
            challenge: video.challenge,
            starving,
            // Null unless this was taken off somebody: the grounds for doing so.
            takenFrom: priorStatus && priorStatus.reason !== 'unclaimed' ? priorStatus : null,
            waitedMs: video.createdAt?.toMillis ? placedNow - video.createdAt.toMillis() : null,
          })
        } finally {
          inFlight.current.delete(video.id)
        }
      }),
    )
  }, [clock, placeClaimed, selectClaimable])

  useEffect(() => {
    if (paused) return
    const id = setInterval(() => {
      tick().catch((err) => modlog('tick-failed', { error: String(err) }))
    }, TICK_MS)
    tick().catch(() => {})
    return () => clearInterval(id)
  }, [paused, tick])

  //--------------------------------------------------------------------------
  // Teardown. Best effort by design: the TTL is what actually guarantees an
  // abandoned claim comes back, because a killed browser runs none of this.

  useEffect(() => {
    const teardown = () => {
      const s = live.current
      if (!s.eventId || s.paused) return
      if (s.heldIds.length) releaseClaimsFast(s.eventId, s.heldIds)
      removePresence(s.eventId, s.sessionId).catch(() => {})
    }
    window.addEventListener('pagehide', teardown)
    return () => {
      window.removeEventListener('pagehide', teardown)
      teardown()
    }
  }, [])

  //--------------------------------------------------------------------------
  // Actions

  const decide = useCallback(
    async (videoId, accepted) => {
      // Belt and braces with the tile's own guard: a decision on a video that
      // is no longer ours would overwrite whatever its real moderator chose.
      const slot = slotsRef.current.find((sl) => sl && sl.video.id === videoId)
      if (slot?.lost) {
        modlog('decide-refused', { sessionId, videoId, reason: 'lost' })
        return
      }
      const decision = accepted ? 'accepted' : 'rejected'
      // Marked before the write, so the video leaving the pending queue reads
      // as "I decided this" rather than "somebody took it from me".
      updateSlots((prev) =>
        prev.map((s) => (s && s.video.id === videoId ? { ...s, decision, lost: false } : s)),
      )
      modlog('decide', { sessionId, videoId, accepted })
      await onDecide(videoId, accepted)
      await releaseClaim(eventId, videoId, sessionId)
    },
    [eventId, sessionId, onDecide, updateSlots],
  )

  const markWatched = useCallback(
    (videoId, verified = true, why) => {
      updateSlots((prev) =>
        prev.map((s) =>
          s && s.video.id === videoId && !s.watched
            ? { ...s, watched: true, watchVerified: verified }
            : s,
        ),
      )
      if (!verified) modlog('watch-unverified', { sessionId, videoId, why })
    },
    [updateSlots, sessionId],
  )

  const pause = useCallback(async () => {
    const s = live.current
    setPaused(true)
    modlog('pause', { sessionId: s.sessionId, released: s.heldIds })
    updateSlots((prev) => prev.map((slot) => (slot && slot.decision === null ? null : slot)))
    if (s.heldIds.length) await releaseClaims(s.eventId, s.heldIds, s.sessionId)
    await removePresence(s.eventId, s.sessionId).catch(() => {})
  }, [updateSlots])

  const resume = useCallback(() => {
    // A paused session is gone, not suspended. A fresh id cannot inherit a
    // stale claim that still names the old one.
    const id = newSessionId()
    setSessionId(id)
    lastHeartbeat.current = 0
    heartbeatLandedAt.current = 0
    setPaused(false)
    modlog('resume', { sessionId: id })
  }, [])

  const togglePaused = useCallback(() => {
    if (live.current.paused) resume()
    else pause()
  }, [pause, resume])

  //--------------------------------------------------------------------------

  /** Everyone working this event right now, me included, for the presence chips. */
  const activeModerators = useMemo(() => {
    const rows = moderators
      .filter((m) => activeSessionIds.has(m.sessionId))
      .map((m) => ({
        ...m,
        load: loadBySession[m.sessionId] || 0,
        isMe: m.sessionId === sessionId,
      }))
    if (!paused && !rows.some((r) => r.isMe)) {
      // Our first heartbeat has not landed yet; show ourselves anyway.
      rows.push({
        sessionId,
        uid: user?.uid,
        displayName: user?.displayName || user?.email || '',
        load: heldIds.length,
        freeSlots,
        challengeFilter,
        isMe: true,
      })
    }
    return rows.sort((a, b) =>
      a.isMe ? -1 : b.isMe ? 1 : a.sessionId < b.sessionId ? -1 : 1,
    )
  }, [
    moderators,
    activeSessionIds,
    loadBySession,
    sessionId,
    paused,
    user,
    heldIds.length,
    freeSlots,
    challengeFilter,
  ])

  return {
    sessionId,
    paused,
    slots,
    available,
    activeModerators,
    serverNow: clock.serverNow,
    heldCount: heldIds.length,
    decide,
    markWatched,
    pause,
    resume,
    togglePaused,
  }
}
